import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Loader2, X, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MEMBER_TYPES = [
  { value: 'partner', label: 'Partner' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'mentor', label: 'Mentor' },
  { value: 'employee', label: 'Employee' },
  { value: 'consultant', label: 'Consultant' }
];

const TEAM_TYPES = [
  { value: 'general_partners', label: 'General Partners' },
  { value: 'venture_partners', label: 'Venture Partners' },
  { value: 'investment_sourcing', label: 'Team - Investment Sourcing' },
  { value: 'investment_management', label: 'Team - Investment Management' },
  { value: 'research_and_strategy', label: 'Team - Research & Strategy' },
  { value: 'investor_relations', label: 'Team - Investor Relations' },
  { value: 'finance', label: 'Team - Finance' },
  { value: 'esg', label: 'Team - ESG' },
  { value: 'investment_growth', label: 'Team - Investment Growth' },
  { value: 'human_resources', label: 'Team - People & Talent' },
  { value: 'efficiency_and_effectiveness', label: 'Team - Efficiency & Effectiveness' },
  { value: 'brand_management', label: 'Team - Brand Management' },
  { value: 'founders_office', label: "Team - MD's Office" },
  { value: 'advisors', label: 'Mentors and Advisors' }
];

const TeamForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [newCapability, setNewCapability] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    member_type: 'employee',
    designation: '',
    team_type: 'investment_sourcing',
    capabilities: [],
    photo_alt: '',
    linkedin_url: '',
    twitter_url: '',
    email: '',
    bio: '',
    three_words: '',
    short_bio: '',
    is_active: true
  });

  const fetchMember = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/team/${id}`, { withCredentials: true });
      setFormData({
        ...response.data,
        capabilities: response.data.capabilities || []
      });
      if (response.data.photo_url) {
        setPhotoPreview(response.data.photo_url.startsWith('/') ? `${BACKEND_URL}${response.data.photo_url}` : response.data.photo_url);
      }
    } catch (error) {
      toast.error('Failed to load member');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing) fetchMember();
  }, [fetchMember, isEditing]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleAddCapability = () => {
    if (newCapability.trim() && !formData.capabilities.includes(newCapability.trim())) {
      setFormData(prev => ({
        ...prev,
        capabilities: [...prev.capabilities, newCapability.trim()]
      }));
      setNewCapability('');
    }
  };

  const handleRemoveCapability = (cap) => {
    setFormData(prev => ({
      ...prev,
      capabilities: prev.capabilities.filter(c => c !== cap)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.designation) {
      toast.error('Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      let response;
      if (isEditing) {
        response = await axios.put(`${API}/admin/team/${id}`, formData, { withCredentials: true });
      } else {
        response = await axios.post(`${API}/admin/team`, formData, { withCredentials: true });
      }
      const memberId = response.data.member_id;

      if (photoFile) {
        const photoFormData = new FormData();
        photoFormData.append('file', photoFile);
        photoFormData.append('alt_text', formData.photo_alt || formData.name);
        await axios.post(`${API}/admin/team/${memberId}/photo`, photoFormData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      }

      toast.success(isEditing ? 'Member updated' : 'Member created');
      navigate('/admin/team');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" /></div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/team')}><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-2xl font-bold">{isEditing ? 'Edit Member' : 'Add Team Member'}</h1>
          <p className="text-gray-500">Fill in the member details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl">
        <div className="bg-white rounded-lg border p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label>Designation *</Label>
              <Input value={formData.designation} onChange={(e) => handleChange('designation', e.target.value)} required />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Photo</Label>
            <div className="flex items-start gap-4">
              {photoPreview && <img src={photoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover" />}
              <div className="flex-1">
                <Input type="file" accept="image/*" onChange={handlePhotoChange} className="mb-2" />
                <Input placeholder="Alt text" value={formData.photo_alt} onChange={(e) => handleChange('photo_alt', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Member Type</Label>
              <Select value={formData.member_type} onValueChange={(v) => handleChange('member_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MEMBER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Team Type</Label>
              <Select value={formData.team_type} onValueChange={(v) => handleChange('team_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TEAM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Capabilities */}
          <div className="space-y-2">
            <Label>Capabilities</Label>
            <p className="text-xs text-gray-500 mb-2">Add expertise areas (e.g., "Deep Tech", "B2B SaaS", "Healthcare")</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.capabilities.map((cap, idx) => (
                <Badge key={idx} variant="secondary" className="gap-1 py-1.5">
                  {cap}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => handleRemoveCapability(cap)} />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add capability..."
                value={newCapability}
                onChange={(e) => setNewCapability(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCapability())}
              />
              <Button type="button" variant="outline" onClick={handleAddCapability}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>LinkedIn URL</Label>
              <Input value={formData.linkedin_url} onChange={(e) => handleChange('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/..." />
            </div>
            <div className="space-y-2">
              <Label>X (Twitter) URL</Label>
              <Input value={formData.twitter_url} onChange={(e) => handleChange('twitter_url', e.target.value)} placeholder="https://x.com/..." />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="email@example.com" />
            </div>
          </div>

          {/* Short Bio */}
          <div className="space-y-2">
            <Label>Short Bio</Label>
            <Textarea 
              value={formData.short_bio} 
              onChange={(e) => handleChange('short_bio', e.target.value)} 
              rows={2}
              placeholder="One-liner description (optional)"
            />
            <p className="text-xs text-gray-500">Brief description shown on the team listing page</p>
          </div>

          {/* Three Words */}
          <div className="space-y-2">
            <Label>3 Words (Optional)</Label>
            <Input
              value={formData.three_words || ''}
              onChange={(e) => handleChange('three_words', e.target.value)}
              placeholder='e.g. "Curious, Builder, Optimist"'
            />
            <p className="text-xs text-gray-500">Shown on the team member page. Enter up to 3 words (comma or space separated).</p>
          </div>

          {/* Full Bio */}
          <div className="space-y-2">
            <Label>Full Bio</Label>
            <Textarea 
              value={formData.bio} 
              onChange={(e) => handleChange('bio', e.target.value)} 
              rows={6}
              placeholder="Detailed biography..."
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch checked={formData.is_active} onCheckedChange={(v) => handleChange('is_active', v)} />
            <Label>Active</Label>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/team')}>Cancel</Button>
          <Button type="submit" className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TeamForm;
